import { useState, useEffect, useRef, useCallback } from "react";
import { imgUrl } from "../../api";
import "./GaleriaPropiedad.css";

// Distancia mínima (px) del dedo para considerar que fue un deslizamiento
const SWIPE_MIN = 50;

const IconoCerrar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
const IconoAnterior = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" />
  </svg>
);
const IconoSiguiente = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 5l7 7-7 7" />
  </svg>
);
const IconoCuadricula = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

export default function GaleriaPropiedad({ imagenes = [], title = "" }) {
  const [abierto, setAbierto] = useState(false);
  const [idx, setIdx] = useState(0);
  const [idxMovil, setIdxMovil] = useState(0);
  const toque = useRef(null);
  const huboSwipe = useRef(false);
  const carruselRef = useRef(null);

  const total = imagenes.length;

  const ir = useCallback(
    (dir) => setIdx((i) => (i + dir + total) % total),
    [total]
  );
  const abrir = (i) => { setIdx(i); setAbierto(true); };
  const cerrar = () => setAbierto(false);

  // Teclado (flechas / Esc) y bloquear el scroll de la página mientras está abierto
  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") ir(1);
      if (e.key === "ArrowLeft") ir(-1);
      if (e.key === "Escape") setAbierto(false);
    };
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflowPrevio;
    };
  }, [abierto, ir]);

  // Precargar la foto anterior y la siguiente para que el cambio sea instantáneo
  useEffect(() => {
    if (!abierto || total < 2) return;
    [idx + 1, idx - 1].forEach((i) => {
      const im = imagenes[(i + total) % total];
      if (im) { const pre = new Image(); pre.src = imgUrl(im.url); }
    });
  }, [abierto, idx, imagenes, total]);

  if (total === 0) return null;

  // ── Deslizar con el dedo en pantalla completa ──
  const onTouchStart = (e) => {
    const t = e.touches[0];
    toque.current = { x: t.clientX, y: t.clientY };
    huboSwipe.current = false;
  };
  const onTouchEnd = (e) => {
    if (!toque.current || total < 2) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - toque.current.x;
    const dy = t.clientY - toque.current.y;
    toque.current = null;
    if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
      huboSwipe.current = true;
      ir(dx < 0 ? 1 : -1); // dedo a la izquierda = siguiente
    }
  };
  // Tocar el fondo oscuro cierra, pero no si el toque fue un deslizamiento
  const onFondoClick = () => {
    if (huboSwipe.current) { huboSwipe.current = false; return; }
    cerrar();
  };

  // Contador del carrusel en celular
  const onScrollMovil = () => {
    const el = carruselRef.current;
    if (!el || !el.clientWidth) return;
    setIdxMovil(Math.round(el.scrollLeft / el.clientWidth));
  };

  const principales = imagenes.slice(0, 5);
  const n = principales.length;
  const srcMedia = (im) => imgUrl(im?.url_medium || im?.url);

  return (
    <>
      {/* ── Computadora / tablet: foto grande + cuadrícula ── */}
      <div className={`gp-grid gp-n${n}`}>
        {principales.map((im, i) => (
          <button
            type="button"
            key={im.id ?? i}
            className={`gp-cell gp-cell-${i}`}
            onClick={() => abrir(i)}
            aria-label={`Ver foto ${i + 1} de ${total}`}
          >
            <img
              src={srcMedia(im)}
              alt={`${title} - foto ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </button>
        ))}
        {total > 1 && (
          <button type="button" className="gp-all-btn" onClick={() => abrir(0)}>
            <IconoCuadricula /> Ver todas las fotos ({total})
          </button>
        )}
      </div>

      {/* ── Celular: carrusel que se desliza con el dedo ── */}
      <div className="gp-movil">
        <div className="gp-carrusel" ref={carruselRef} onScroll={onScrollMovil}>
          {imagenes.map((im, i) => (
            <button
              type="button"
              key={im.id ?? i}
              className="gp-slide"
              onClick={() => abrir(i)}
              aria-label={`Ver foto ${i + 1} de ${total} en pantalla completa`}
            >
              <img
                src={srcMedia(im)}
                alt={`${title} - foto ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                draggable={false}
              />
            </button>
          ))}
        </div>
        {total > 1 && (
          <span className="gp-contador-movil">{idxMovil + 1} / {total}</span>
        )}
      </div>

      {/* ── Pantalla completa ── */}
      {abierto && (
        <div
          className="gp-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Fotos de ${title}`}
          onClick={onFondoClick}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <span className="gp-lb-contador">{idx + 1} / {total}</span>

          <button
            type="button"
            className="gp-lb-btn gp-lb-cerrar"
            onClick={(e) => { e.stopPropagation(); cerrar(); }}
            aria-label="Cerrar"
          >
            <IconoCerrar />
          </button>

          {total > 1 && (
            <button
              type="button"
              className="gp-lb-btn gp-lb-nav gp-lb-prev"
              onClick={(e) => { e.stopPropagation(); ir(-1); }}
              aria-label="Foto anterior"
            >
              <IconoAnterior />
            </button>
          )}

          <img
            key={idx}
            src={imgUrl(imagenes[idx]?.url)}
            alt={`${title} - foto ${idx + 1}`}
            className="gp-lb-img"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />

          {total > 1 && (
            <button
              type="button"
              className="gp-lb-btn gp-lb-nav gp-lb-next"
              onClick={(e) => { e.stopPropagation(); ir(1); }}
              aria-label="Foto siguiente"
            >
              <IconoSiguiente />
            </button>
          )}
        </div>
      )}
    </>
  );
}
